// "Mark handled" on public.leads (AFTERNOON-2026-10-01 B4).
//
// WHAT IT WRITES, IN FULL
//   mark    { processed: true,  processed_at: '<ISO now>', processed_note: '<note>'|null }
//   unmark  { processed: false, processed_at: null,        processed_note: null }
// on public.leads, filtered by primary key (`id in (...)`), and nothing else. Every
// other column of public.leads is the leads worker's.
//
// WHO READS IT
// pt-agents agents/morning_brief.py: `processed is True` drops a lead from every
// "Leads with no contact" block (A call list, B alerted, C reach by email). That
// is the whole point of the button: a lead a recruiter has dealt with stops being
// listed every morning. The brief reads ONLY a literal true, so this module writes
// a literal boolean and reads the same way (isHandled).
//
// THE COLUMNS MAY NOT BE THERE YET
// `processed` has existed since 2026-06-10. `processed_at` and `processed_note` are
// added by migrations/2026-10-01-leads-processed-note.sql, which is applied by hand
// on Matt's go. Until then:
//   * the probe (probeHandledColumns) reports them absent and the UI hides the note
//     input, so nobody types a note that cannot be stored;
//   * the write sends only { processed } when the probe said absent, and if the
//     probe said present (or never answered) but the write still hits a
//     missing-column error, it retries ONCE with only { processed } and reports
//     `degraded: true`, so the toast can say the time and note were not saved.
// The retry is only ever for a missing column. An RLS denial, a network failure or
// anything else surfaces unchanged: a recruiter must never believe a lead was
// marked when nothing was written.
//
// NOTHING IS UPDATED SILENTLY TO ZERO ROWS
// With RLS on, an UPDATE the policy does not admit is not an error: PostgREST
// answers 200 with no rows. (Signed out, the anon key reaches exactly this case:
// anon holds a table UPDATE grant but no policy.) So every write asks for the
// updated ids back (`.select('id')`) and throws if they are not exactly the ids it
// was given.

export const LEADS_TABLE = 'leads';
// = the database check leads_processed_note_len (migrations/2026-10-01-leads-processed-note.sql), per Matt's GATE B
// ruling (AFTERNOON-2026-10-01): one limit, enforced in both places, the UI showing a live counter.
export const NOTE_MAX = 500;
/** Characters as the database's char_length counts them (code points), for the counter. */
export const noteLength = (s) => Array.from(String(s || '')).length;
export const MAX_BULK = 100;
export const OPTIONAL_COLUMNS = ['processed_at', 'processed_note'];

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// ---------------------------------------------------------------------- reading

/** A lead is handled only when processed is a literal true -- the brief's rule. */
export function isHandled(row) {
  return !!row && row.processed === true;
}

/** The moment it was handled, or null (unhandled, or handled before the migration). */
export function handledAt(row) {
  if (!isHandled(row) || !row.processed_at) return null;
  const d = new Date(row.processed_at);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * The Leads view's "needs attention" predicate, shared with the nav badge so the
 * two cannot disagree. A handled lead needs nobody, exactly as in the brief.
 */
export function needsAttention(row, hasCandidateMatch, isNeedsHuman) {
  return isNeedsHuman(row) && !hasCandidateMatch && !isHandled(row);
}

/** Filter values for the view's "Handled" select. */
export const HANDLED_FILTERS = ['Any status', 'Unhandled', 'Handled'];

export function passesHandledFilter(row, filter) {
  if (filter === 'Unhandled') return !isHandled(row);
  if (filter === 'Handled') return isHandled(row);
  return true;
}

// ---------------------------------------------------------------------- writing

/**
 * One line, trimmed, at most NOTE_MAX characters; '' becomes null. Newlines and
 * runs of whitespace collapse to one space -- the brief and the CRM both print it
 * on a single line. Over-long notes are TRUNCATED, not refused: the recruiter has
 * already decided the lead is handled, and losing the action over the note's
 * length would be the worse outcome. The input's maxLength stops it in the UI.
 */
export function normalizeNote(note) {
  if (note === null || note === undefined) return null;
  const one = String(note).replace(/\s+/g, ' ').trim();
  if (!one) return null;
  return Array.from(one).slice(0, NOTE_MAX).join('');
}

/**
 * The patch body. `now` is injected so a test can pin it; production gets the
 * real clock. processed_at is taken client-side: this is a PATCH through
 * PostgREST from a browser and the column has no default by design (a default
 * would backfill every historical row with the migration's timestamp).
 */
export function handledPatch(handled, note, now = () => new Date()) {
  return handled
    ? { processed: true, processed_at: now().toISOString(), processed_note: normalizeNote(note) }
    : { processed: false, processed_at: null, processed_note: null };
}

/** The pre-migration patch: the only column guaranteed to exist. */
export function minimalPatch(handled) {
  return { processed: !!handled };
}

/**
 * Does a PostgREST error mean "processed_at / processed_note is not there"?
 *   PGRST204  PostgREST's schema cache does not know a column in a PATCH body
 *   42703     Postgres undefined_column (a SELECT naming it)
 * `processed` itself exists, so these codes on this table can only be about the
 * two new columns -- but when the message names a column, it must be one of them.
 */
export function isMissingHandledColumn(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const msg = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`.toLowerCase();
  const namesOurs = OPTIONAL_COLUMNS.some((c) => msg.includes(c));
  if (code === 'PGRST204' || code === '42703') {
    return namesOurs || !/column/.test(msg) || !msg.trim();
  }
  if (!namesOurs) return false;
  return msg.includes('does not exist') || msg.includes('could not find') || msg.includes('schema cache');
}

/** Every lead id in a block of text, lower-cased, de-duplicated, in order. */
export function parseLeadIds(text) {
  const seen = new Set();
  const out = [];
  for (const m of String(text || '').matchAll(UUID_RE)) {
    const id = m[0].toLowerCase();
    if (!seen.has(id)) { seen.add(id); out.push(id); }
  }
  return out;
}

function normIds(ids) {
  const list = [...new Set((ids || []).map((x) => String(x)))];
  if (list.length === 0) throw new Error('No leads given.');
  if (list.length > MAX_BULK) throw new Error(`At most ${MAX_BULK} leads at a time (got ${list.length}).`);
  return list;
}

/**
 * Probe once whether processed_at + processed_note exist. Zero rows cross the
 * wire (`head: true`, `limit(0)`); PostgREST still has to resolve the column names
 * to answer, so a missing column errors. ANY error reads as absent -- the note
 * input is hidden, which is the safe direction.
 */
export async function probeHandledColumns(client) {
  try {
    const { error } = await client.from(LEADS_TABLE)
      .select(OPTIONAL_COLUMNS.join(','), { head: true })
      .limit(0);
    return !error;
  } catch (_) {
    return false;
  }
}

/**
 * Set (or clear) handled on exactly `ids`. Throws on any failure.
 * @param client  a supabase-js client
 * @param opts.noteColumns  true/false from the probe; undefined = unknown
 * @returns {{ patch, updatedIds: string[], degraded: boolean }}
 *   degraded = only { processed } was written (migration not applied yet).
 */
export async function setLeadsHandled(client, ids, handled, note, opts = {}) {
  const list = normIds(ids);
  const full = handledPatch(handled, note, opts.now);
  const send = async (patch) => client.from(LEADS_TABLE).update(patch).in('id', list).select('id');

  let patch = opts.noteColumns === false ? minimalPatch(handled) : full;
  let degraded = patch !== full;
  let { data, error } = await send(patch);
  if (error && !degraded && isMissingHandledColumn(error)) {
    patch = minimalPatch(handled);
    degraded = true;
    ({ data, error } = await send(patch));
  }
  if (error) throw error;

  const updatedIds = (data || []).map((r) => String(r.id));
  const missing = list.filter((id) => !updatedIds.includes(id));
  if (missing.length) {
    const err = new Error(
      `Only ${updatedIds.length} of ${list.length} lead(s) were updated. ` +
      'If you are signed in, the others no longer exist; if not, sign in and retry.');
    err.code = 'not_all_updated';
    err.updatedIds = updatedIds;
    err.patch = patch;
    throw err;
  }
  return { patch, updatedIds, degraded };
}

export const markLeadHandled = (client, id, note, opts) => setLeadsHandled(client, [id], true, note, opts);
export const unmarkLeadHandled = (client, id, opts) => setLeadsHandled(client, [id], false, null, opts);
export const markLeadsHandled = (client, ids, note, opts) => setLeadsHandled(client, ids, true, note, opts);
export const unmarkLeadsHandled = (client, ids, opts) => setLeadsHandled(client, ids, false, null, opts);

// ------------------------------------------------------------------ view state

/** Fold a completed write into the rendered list. New array; input untouched. */
export function applyHandled(rows, ids, patch) {
  if (!Array.isArray(rows)) return [];
  const set = new Set((ids || []).map(String));
  return rows.map((r) => (r && set.has(String(r.id)) ? { ...r, ...patch } : r));
}

/**
 * Split requested ids against the loaded leads. Only leads whose state would
 * actually change are written, so an undo of the batch restores exactly what the
 * batch changed -- an already-handled lead keeps its original time and note.
 */
export function planHandled(rows, ids, handled) {
  const byId = new Map((rows || []).filter(Boolean).map((r) => [String(r.id), r]));
  const change = [], already = [], unknown = [];
  for (const id of [...new Set((ids || []).map(String))]) {
    const row = byId.get(id);
    if (!row) unknown.push(id);
    else if (isHandled(row) === !!handled) already.push(id);
    else change.push(id);
  }
  return { change, already, unknown };
}

function leadsNoun(n) { return `${n} lead${n === 1 ? '' : 's'}`; }

/**
 * The whole action, as the view runs it. Never throws.
 * @param api  { mark(ids, note), unmark(ids) } -> setLeadsHandled's result
 * @returns ok:   { ok: true, changed, patch, degraded, plan, message }
 *          fail: { ok: false, changed: [] | partial ids, patch|null, plan, message }
 * On failure `changed` is empty unless the server reported a partial update, so a
 * caller that folds only `changed` leaves its state untouched on a plain error.
 */
export async function runHandled({ api, rows, ids, handled, note }) {
  const plan = planHandled(rows, ids, handled);
  const skipped = [
    plan.already.length ? `${plan.already.length} already ${handled ? 'handled' : 'unhandled'}` : '',
    plan.unknown.length ? `${plan.unknown.length} not in the loaded leads` : '',
  ].filter(Boolean).join(', ');
  if (plan.change.length === 0) {
    return { ok: false, changed: [], patch: null, plan,
      message: `Nothing to change${skipped ? ` (${skipped})` : ''}.` };
  }
  try {
    const res = handled ? await api.mark(plan.change, note) : await api.unmark(plan.change);
    const verb = handled ? 'Marked' : 'Undone:';
    const tail = handled ? 'handled' : 'back to unhandled';
    return {
      ok: true, changed: res.updatedIds, patch: res.patch, degraded: !!res.degraded, plan,
      message: `${verb} ${leadsNoun(res.updatedIds.length)} ${tail}` +
        (skipped ? ` (skipped ${skipped})` : '') +
        (res.degraded && handled ? ' -- time and note not saved: the processed_note migration is not applied yet' : '') +
        '.',
    };
  } catch (e) {
    const partial = (e && Array.isArray(e.updatedIds)) ? e.updatedIds : [];
    return { ok: false, changed: partial, patch: partial.length ? e.patch : null, plan,
      message: (e && e.message) || 'Could not update the lead(s).' };
  }
}

/**
 * runHandled + the fold into view state, exactly as the Leads view wires it.
 * setRows receives an updater and is called ONLY when rows actually changed
 * server-side (success, or a reported partial update), so a plain error leaves the
 * view's state untouched. setUndo gets the batch to offer Undo for (marks only).
 */
export async function performHandled({ api, rows, ids, handled, note, setRows, setUndo, notify }) {
  const res = await runHandled({ api, rows, ids, handled, note });
  if (res.changed.length && res.patch) {
    const { changed, patch } = res;
    setRows((prev) => applyHandled(prev, changed, patch));
  }
  if (res.ok && setUndo) setUndo(handled ? { ids: res.changed, message: res.message } : null);
  if (notify) notify(res.message);
  return res;
}

/** The bulk confirm. The count is the first thing it says. */
export function bulkConfirmText(n, note) {
  const nn = normalizeNote(note);
  return `Mark ${leadsNoun(n)} as handled?\n\n` +
    'Nothing is sent to anyone. Each lead drops off the morning brief\'s lists' +
    (nn ? `, with the note: "${nn}"` : '') + '. You can undo this batch afterwards.';
}

/** Header list: a select column first, a Handled column before Actions. */
export function withHandledColumns(cols) {
  const base = (cols || []).filter((c) => c !== 'Actions');
  return ['Select', ...base, 'Handled', ...((cols || []).includes('Actions') ? ['Actions'] : [])];
}
